import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, type ReactNode } from 'react';

import { useProfileNavSection } from '@/core/store/profileNavigation/profileNavigationSelectors';
import { useTouchEditVisible } from '@/core/store/touchEdit/touchEditSelectors';
import { mergeSx } from '@/utils/mergeSx';

import { sectionBlockStyles as sx } from './SectionBlock.styles';

type Props = {
	id?: string;
	title: string;
	children: ReactNode;
	hideHeader?: boolean;
	controls?: ReactNode;
	offsetControls?: boolean;
	controlsAlwaysVisible?: boolean;
	titleAdornment?: ReactNode;
	customSx?: {
		root?: SxProps<Theme>;
		title?: SxProps<Theme>;
		section?: SxProps<Theme>;
		controls?: SxProps<Theme>;
	};
};

export const SectionBlock = ({
	id,
	title,
	children,
	hideHeader = false,
	controls,
	offsetControls = false,
	controlsAlwaysVisible = false,
	titleAdornment,
	customSx
}: Props) => {
	const { addSection, removeSection } = useProfileNavSection();
	const touchEditVisible = useTouchEditVisible();

	useEffect(() => {
		addSection({
			id: id ?? title,
			title: title
		});

		return () => {
			removeSection(id ?? title);
		};
	}, [id, title, addSection, removeSection]);

	return (
		<Box
			sx={mergeSx(sx.root(touchEditVisible), customSx?.root)}
			id={id ?? title}
		>
			{!hideHeader && (
				<Box sx={sx.headerRow}>
					<Box sx={sx.titleGroup}>
						<Typography
							variant='subtitle1'
							sx={mergeSx(sx.title, customSx?.title)}
						>
							{title}
						</Typography>
						{titleAdornment && (
							<Box component='span' sx={sx.titleAdornment}>
								{titleAdornment}
							</Box>
						)}
					</Box>
					{controls && (
						<Box
							sx={mergeSx(sx.controls(offsetControls), customSx?.controls)}
							data-section-controls
							{...(controlsAlwaysVisible ? { 'data-force-visible': true } : {})}
						>
							{controls}
						</Box>
					)}
				</Box>
			)}
			<Box sx={mergeSx(sx.section, customSx?.section)}>{children}</Box>
		</Box>
	);
};
